import React from 'react';
import { Box } from '@mui/material';

export default function UniversalIDrawing({ section }) {
  if (!section) return null;

  // Use the same four properties as welded; ignore r1
  const { d: height, bf: width, tf: flangeThickness, tw: webThickness } = section.properties;

  const CANVAS_WIDTH = 800;
  const CANVAS_HEIGHT = 1200;
  const MARGIN = 100;

  const scaleX = (CANVAS_WIDTH - 2 * MARGIN) / width;
  const scaleY = ((CANVAS_HEIGHT / 2) - 2 * MARGIN) / height;
  const scale = Math.min(scaleX, scaleY) * 0.95;

  const scaledHeight = height * scale;
  const scaledWidth = width * scale;
  const scaledFlangeThickness = flangeThickness * scale;
  const scaledWebThickness = webThickness * scale;

  const centerX = CANVAS_WIDTH / 2;
  const topCenterY = CANVAS_HEIGHT / 4;
  const bottomCenterY = (3 * CANVAS_HEIGHT) / 4;

  const textStyle = {
    fontSize: '32px',
    fontFamily: 'Arial',
    fontWeight: '500',
  };

  return (
    <Box sx={{ width: '100%', height: '900px' }}>
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* --- Top Drawing: Section with dimensions --- */}
        <path
          d={`
            M ${centerX - scaledWidth/2} ${topCenterY - scaledHeight/2}
            H ${centerX + scaledWidth/2}
            V ${topCenterY - scaledHeight/2 + scaledFlangeThickness}
            H ${centerX + scaledWebThickness/2}
            V ${topCenterY + scaledHeight/2 - scaledFlangeThickness}
            H ${centerX + scaledWidth/2}
            V ${topCenterY + scaledHeight/2}
            H ${centerX - scaledWidth/2}
            V ${topCenterY + scaledHeight/2 - scaledFlangeThickness}
            H ${centerX - scaledWebThickness/2}
            V ${topCenterY - scaledHeight/2 + scaledFlangeThickness}
            H ${centerX - scaledWidth/2}
            Z
          `}
          fill="#F0F0F0"
          stroke="black"
          strokeWidth="2"
        />

        {/* Width dimension */}
        <line
          x1={centerX - scaledWidth/2}
          y1={topCenterY - scaledHeight/2 - 40}
          x2={centerX + scaledWidth/2}
          y2={topCenterY - scaledHeight/2 - 40}
          stroke="black"
          strokeWidth="2"
          markerEnd="url(#arrow)"
          markerStart="url(#arrow)"
        />
        <text
          x={centerX}
          y={topCenterY - scaledHeight/2 - 80}
          textAnchor="middle"
          {...textStyle}
        >
          {width} mm
        </text>

        {/* Height dimension */}
        <line
          x1={centerX + scaledWidth/2 + 40}
          y1={topCenterY - scaledHeight/2}
          x2={centerX + scaledWidth/2 + 40}
          y2={topCenterY + scaledHeight/2}
          stroke="black"
          strokeWidth="2"
          markerEnd="url(#arrow)"
          markerStart="url(#arrow)"
        />
        <text
          x={centerX + scaledWidth/2 + 90}
          y={topCenterY}
          textAnchor="middle"
          transform={`rotate(90, ${centerX + scaledWidth/2 + 90}, ${topCenterY})`}
          {...textStyle}
        >
          {height} mm
        </text>

        {/* Flange thickness */}
        <text
          x={centerX - scaledWidth/2 - 35}
          y={topCenterY - scaledHeight/2 + scaledFlangeThickness/2}
          textAnchor="end"
          {...textStyle}
          dominantBaseline="middle"
        >
          tf = {flangeThickness} mm
        </text>

        {/* Web thickness */}
        <text
          x={centerX - scaledWebThickness/2 - 15}
          y={topCenterY}
          textAnchor="end"
          {...textStyle}
          dominantBaseline="middle"
        >
          tw = {webThickness} mm
        </text>

        {/* --- Bottom Drawing: Section with X/Y axes --- */}
        <path
          d={`
            M ${centerX - scaledWidth/2} ${bottomCenterY - scaledHeight/2}
            H ${centerX + scaledWidth/2}
            V ${bottomCenterY - scaledHeight/2 + scaledFlangeThickness}
            H ${centerX + scaledWebThickness/2}
            V ${bottomCenterY + scaledHeight/2 - scaledFlangeThickness}
            H ${centerX + scaledWidth/2}
            V ${bottomCenterY + scaledHeight/2}
            H ${centerX - scaledWidth/2}
            V ${bottomCenterY + scaledHeight/2 - scaledFlangeThickness}
            H ${centerX - scaledWebThickness/2}
            V ${bottomCenterY - scaledHeight/2 + scaledFlangeThickness}
            H ${centerX - scaledWidth/2}
            Z
          `}
          fill="#F0F0F0"
          stroke="black"
          strokeWidth="2"
        />

        {/* X-axis (dashed) */}
        <line
          x1={centerX - scaledWidth/2 - 30}
          y1={bottomCenterY}
          x2={centerX + scaledWidth/2 + 30}
          y2={bottomCenterY}
          stroke="black"
          strokeWidth="1"
          strokeDasharray="5,5"
        />
        {/* Y-axis (dashed) */}
        <line
          x1={centerX}
          y1={bottomCenterY - scaledHeight/2 - 30}
          x2={centerX}
          y2={bottomCenterY + scaledHeight/2 + 30}
          stroke="black"
          strokeWidth="1"
          strokeDasharray="5,5"
        />

        {/* Axis labels */}
        <text
          x={centerX + scaledWidth/2 + 50}
          y={bottomCenterY - 10}
          textAnchor="start"
          fontSize="32"
          fontFamily="Arial"
          fontWeight="bold"
        >
          X
        </text>
        <text
          x={centerX + 10}
          y={bottomCenterY - scaledHeight/2 - 40}
          textAnchor="start"
          fontSize="32"
          fontFamily="Arial"
          fontWeight="bold"
        >
          Y
        </text>

        {/* Arrow marker definition */}
        <defs>
          <marker
            id="arrow"
            viewBox="0 0 10 10"
            refX="5"
            refY="5"
            markerWidth="10"
            markerHeight="10"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="black" />
          </marker>
        </defs>
      </svg>
    </Box>
  );
}
